<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import AppSelect from '@/components/ui/AppSelect.vue'
import Toggle from '@/components/ui/Toggle.vue'
import { pinia } from '@/stores'
import { useRouterStore } from '@/stores/router'
import { onMounted, ref } from 'vue'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { storeToRefs } from 'pinia'
import { getCustomerAddress } from '@/services/customer'
import Modal from '@/components/ui/Modal.vue'
import { AddressTypes, type Address } from '@/types/remote-diagnose'
import { getStore, removeStore } from '@/utils/remote-diagnose'
import { getCountries } from '@/services/carInfo'
import DongleAddressContent from '@/lang/address.json'
import { useLang } from '@/composables/use-lang'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  }
})

const { push } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)
const { changePaymentMethods, resetPaymentGateway } = remoteDiagnoseStore
const { state } = storeToRefs(remoteDiagnoseStore)

const langContent = useLang(DongleAddressContent)

const modal = ref<InstanceType<typeof Modal> | null>(null)
const isVisible = ref<boolean>(false)
const useIdeal = ref<boolean>(true)
const countries = ref([])
const addressesConfirmed = ref<boolean>(false)
const checksError = ref<boolean>(false)

const emit = defineEmits(['scroll'])

const onSubmit = () => {
  if(!addressesConfirmed.value || !state.value.terms_and_conditions_accepted) {
    checksError.value = true
    return
  }
  checksError.value = false
  modal.value?.open()
}

const handleNext = () => {
  state.value.has_dongle = true
  state.value.dongle_tested = true
  push('schedule-appointment')
}

const goPayment = () => {
  const route = state.value.payment_gateway === 'stripe' ? 'payment' : 'payment-revolut' 
  push(route)
}

const handleIdeal = (e: Event) => {
  const el = e.target as HTMLInputElement

  changePaymentMethods(el.checked)

  useIdeal.value = el.checked
}

onMounted(async () => {
  emit('scroll')

  if (getStore()) {
    removeStore()
    resetPaymentGateway()
    return null
  }

  if (!state.value.new_client) {
    try {
      const result = await getCustomerAddress(state.value.client_name, props.aws_url)

      if (result.data?.length) {
        const address_shipping = result.data.find(
          (address: Address) => address.address_type === AddressTypes.SHIPPING && !address.disabled
        )
        const address_billing = result.data.find(
          (address: Address) => address.address_type === AddressTypes.BILLING && !address.disabled
        )

        if (address_shipping) {
          state.value.address_shipping = address_shipping
        }

        if (address_billing) {
          state.value.address_billing = address_billing
          isVisible.value = true
        }
      }
    } catch (e) {
      console.log('e => ', e)
    }
  }

  const resCountries = await getCountries(props.aws_url)

  if (resCountries) countries.value = resCountries
})
</script>

<template>
  <form @submit.prevent="onSubmit" class="flex flex-col items-center text-center">
    <p>{{ langContent.shipping_address_question }}</p>
    <div class="flex flex-col mt-10 w-full items-center">
      <h2 class="mb-5">{{ langContent.shipping_address_heading }}</h2>
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.company_placeholder"
        v-model="state.address_shipping.company_name"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.name_placeholder"
        v-model="state.client_name"
        required
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.address1_placeholder"
        v-model="state.address_shipping.address_line1"
        required
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.address2_placeholder"
        v-model="state.address_shipping.address_line2"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.zip_placeholder"
        v-model="state.address_shipping.postal_code"
        required
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.city_placeholder"
        v-model="state.address_shipping.city"
        required
      />
      <app-select
        class="mb-5"
        :label="langContent.country_placeholder"
        v-model="state.address_shipping.country"
        :options="countries.length ? countries : [state.address_shipping.country]"
        required
      />
      <input
        class="mb-5"
        type="email"
        :placeholder="langContent.email_placeholder"
        v-model="state.client_email"
        required
      />
    </div>
    <div class="flex flex-col md:flex-row max-w-lg w-full items-center md:justify-evenly">
      <p>{{ langContent.billing_address_question }}</p>
      <toggle class="mb-5" :isVisible="isVisible" v-model="isVisible" />
    </div>
    <div class="flex flex-col mt-10 w-full items-center" v-if="isVisible">
      <h2 class="mb-5">{{ langContent.billing_address_heading }}</h2>
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.company_placeholder"
        v-model="state.address_billing.company_name"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.name_placeholder"
        v-model="state.client_name"
        :required="isVisible"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.address1_placeholder"
        v-model="state.address_billing.address_line1"
        :required="isVisible"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.address2_placeholder"
        v-model="state.address_billing.address_line2"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.zip_placeholder"
        v-model="state.address_billing.postal_code"
        :required="isVisible"
      />
      <input
        class="mb-5"
        type="text"
        :placeholder="langContent.city_placeholder"
        v-model="state.address_billing.city"
        :required="isVisible"
      />
      <app-select
        class="mb-5"
        :label="langContent.country_placeholder"
        v-model="state.address_billing.country"
        :options="countries.length ? countries : [state.address_billing.country]"
        :required="isVisible"
      />
    </div>

    <p>{{ langContent.payment_method_question }}</p>

    <toggle class="mt-5 mb-10" :isVisible="!useIdeal" v-model="useIdeal" @change="handleIdeal" onLabel="Ideal" offLabel="Card" />

    <div class="flex flex-col mb-4">
      <div class=" flex justify-start flex-col-reverse md:flex-row items-center">
        <input
          id="confirm-addresses"
          type="checkbox"
          :class="`md:mr-5 w-5 h-5 ${!addressesConfirmed ? 'appearance-none !border !border-english-vermillion' : ''}`"
          v-model="addressesConfirmed"
        />
        <label for="confirm-addresses">{{ langContent.confirm_addresses_label }}</label>
      </div>

      <div class="mt-5 flex justify-start flex-col-reverse md:flex-row items-center">
        <input
          id="terms-and-conditions"
          type="checkbox"
          :class="`md:mr-5 w-5 h-5 ${!state.terms_and_conditions_accepted ? 'appearance-none !border !border-english-vermillion' : ''}`"
          v-model="state.terms_and_conditions_accepted"
        />
        <label for="terms-and-conditions"
          >{{ langContent.terms_and_conditions_label }}
          <a
            class="text-english-vermillion"
            :href="langContent.terms_and_conditions_link"
            target="_blank"
            >{{ langContent.terms_and_conditions_link_label }}</a
          >
        </label>
      </div>
    </div>

    <span v-if="checksError" class="text-english-vermillion">{{ langContent.error_checkboxes_required }}</span>

    <app-button type="submit">{{
      langContent.submit_button
    }}</app-button>

    <modal ref="modal">
      <p>{{ langContent.modal_question }}</p>

      <div class="flex justify-evenly mt-10">
        <app-button class="w-20" type="button" @click="handleNext">{{
          langContent.modal_yes_button
        }}</app-button>
        <app-button class="w-20" type="button" @click="goPayment">{{
          langContent.modal_no_button
        }}</app-button>
      </div>
    </modal>
  </form>
</template>

<style scoped></style>
